import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import * as API from '../api/courses';
import { Course, CreateCourseData, UpdateCourseData, CourseFilters } from '../api/courses';

interface CoursesContextType {
    courses: Course[];
    isLoading: boolean;
    error: string | null;
    addCourse: (data: CreateCourseData) => Promise<Course>;
    updateCourse: (id: string, data: UpdateCourseData) => Promise<Course>;
    deleteCourse: (id: string) => Promise<void>;
    refreshCourses: (filters?: CourseFilters) => Promise<void>;
    getCourseById: (id: string) => Course | undefined;
}

const CoursesContext = createContext<CoursesContextType | undefined>(undefined);

export const useCourses = () => {
    const context = useContext(CoursesContext);
    if (context === undefined) {
        throw new Error('useCourses must be used within a CoursesProvider');
    }
    return context;
};

interface CoursesProviderProps {
    children: ReactNode;
}

export const CoursesProvider: React.FC<CoursesProviderProps> = ({ children }) => {
    const [courses, setCourses] = useState<Course[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refreshCourses = useCallback(async (filters?: CourseFilters) => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await API.getCourses(filters);
            setCourses(data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erreur lors du chargement des cours');
            console.error('Error fetching courses:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshCourses();
    }, [refreshCourses]);

    const addCourse = async (data: CreateCourseData): Promise<Course> => {
        try {
            const newCourse = await API.createCourse(data);
            setCourses(prev => [...prev, newCourse]);
            return newCourse;
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Erreur lors de la création du cours');
        }
    };

    const updateCourse = async (id: string, data: UpdateCourseData): Promise<Course> => {
        try {
            const updatedCourse = await API.updateCourse(id, data);
            setCourses(prev => prev.map(course => course.id === id ? updatedCourse : course));
            return updatedCourse;
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Erreur lors de la mise à jour du cours');
        }
    };

    const deleteCourse = async (id: string): Promise<void> => {
        try {
            await API.deleteCourse(id);
            setCourses(prev => prev.filter(course => course.id !== id));
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Erreur lors de la suppression du cours');
        }
    };

    const getCourseById = (id: string): Course | undefined => {
        return courses.find(course => course.id === id);
    };

    const value: CoursesContextType = {
        courses,
        isLoading,
        error,
        addCourse,
        updateCourse,
        deleteCourse,
        refreshCourses,
        getCourseById,
    };

    return (
        <CoursesContext.Provider value={value}>
            {children}
        </CoursesContext.Provider>
    );
};

export type { Course, CreateCourseData, UpdateCourseData };
